import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import { ReactNode } from 'react';
import { Order, OrderStatus, PaymentMethodType } from '../../../../models';
import DateTime from '../../../../utils/DateTime';

const useStyles = makeStyles(({
  actionRequiredContainer: {
    backgroundColor: '#FFF4E6',
    border: '1px solid #FFB366',
    borderRadius: 6,
    paddingLeft: 12,
    paddingRight: 12,
    marginTop: 16,
  },
  actionRequiredText: {
    fontSize: 14,
    color: '#92400E',
    marginBottom: 8,
  },
  actionRequiredSubText: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 0,
  },
}));

export const OrderSummarize = ({ order }: { order: Order }) => {
  const classes = useStyles();
  const intl = useIntl();
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PAID:
        return '#0F8461';
      case OrderStatus.CANCEL:
      case OrderStatus.FAIL:
      case OrderStatus.PASS_DUE:
        return '#D14343';
      default:
        return '#000000';
    }
  };

  const renderOrderSummarizeItem = (itemId: string, item: ReactNode) => (
    <Box
      id={`order-${order.id}-${itemId}`}
      display="flex"
      flexDirection="row"
      paddingY={0.5}
      alignItems="center"
      alignContent="center"
    >
      <Typography
        fontSize={16}
        fontWeight={700}
        lineHeight="180%"
        width={150}
        marginX={2}
      >
        <FormattedMessage id={`page.order.history.order.${itemId}`} />
      </Typography>
      <Box
        paddingY={0.25}
      >
        {item}
      </Box>
    </Box>
  );

  const renderRequiredAction = () => (
    <div className={classes.actionRequiredContainer}>
      <p className={classes.actionRequiredText}>
        <FormattedMessage
          id="page.order.history.order.action-require.title"
          values={{
            title: (
              <strong>Action Required:</strong>
            ),
          }}
        />
      </p>
      <p className={classes.actionRequiredSubText}>
        <FormattedMessage id="page.order.history.order.action-require.description" />
      </p>
    </div>
  );

  const statusColor = getStatusColor(order.status);
  const checkRequiredActionOrder = order.status === OrderStatus.PENDING
    && order.paymentMethodType === PaymentMethodType.CREDIT_CARD
    && !!order.stripeInvoiceUrl;

  return (
    <Box
      id={`order-${order.id}-summarize`}
      display="flex"
      flexDirection="column"
      p={3}
    >
      {/* order status */}
      {renderOrderSummarizeItem(
        'status',
        (<Chip
          label={(
            <Typography variant="body1" fontWeight={500} fontSize={14} color={statusColor}>
              <FormattedMessage id={`page.order.history.order.status.${order.status.toLowerCase()}.name`} />
            </Typography>
          )}
          size="small"
          variant="outlined"
          sx={theme => ({
            borderColor: statusColor,
            borderRadius: 2,
            padding: theme.spacing(0.25, 1, 0.5, 1),
          })}
        />),
      )}

      {/* order id */}
      {renderOrderSummarizeItem(
        'id',
        (
          <Typography variant="body1" fontWeight={500} fontSize={16}>
            {order.id}
          </Typography>
        ),
      )}

      {/* order date */}
      {renderOrderSummarizeItem(
        'date',
        (
          <Typography variant="body1" fontWeight={500} fontSize={16}>
            {DateTime.formatDate(intl, order.orderDate)}
          </Typography>
        ),
      )}

      {/* order payment method */}
      {renderOrderSummarizeItem(
        'payment-method',
        (
          <Typography variant="body1" fontWeight={500} fontSize={16}>
            <FormattedMessage
              id={`page.order.history.order.payment-method.${order.paymentMethodType.toLowerCase()}.name`}
            />
          </Typography>
        ),
      )}

      {checkRequiredActionOrder && renderRequiredAction()}
    </Box>
  );
};
